// client/src/utils/markdownRenderer.js
import { sanitizeUrl } from './sanitize';
// Markdown formatting system for Storykin application
//
// 📚 COMPLETE DOCUMENTATION: See docs/FEATURES.md (Markdown System section)
// 🚀 QUICK START: import { renderMarkdownToHTML } from '../utils/markdownRenderer'
// 💡 USAGE: <div dangerouslySetInnerHTML={renderMarkdownToHTML(content)} />
// 
// This utility converts simple markdown-style formatting to HTML for display
// throughout the application. It's designed to be lightweight, secure, and
// easy to extend.
//
// ============================================================================
// MARKDOWN SYNTAX SUPPORTED
// ============================================================================
//
// **bold text**     → <strong>bold text</strong>
// *italic text*     → <em>italic text</em>
// __underlined__    → <u>underlined</u>
// ### Headline      → <h4>Headline</h4>
// - bullet point    → <ul><li>bullet point</li></ul>
// 1. numbered item  → <ol><li>numbered item</li></ol>
// > quote text      → <blockquote>quote text</blockquote>
// ---               → <hr>
// [text](url)       → <a href="url">text</a>
// \n                → <br>
//
// ============================================================================
// USAGE EXAMPLES
// ============================================================================
//
// 1. Basic formatting:
//    Input:  "Learn the **essential skills** and *techniques* to craft your story"
//    Output: "Learn the <strong>essential skills</strong> and <em>techniques</em> to craft your story"
//
// 2. Mixed formatting:
//    Input:  "This is **bold**, this is *italic*, and this is __underlined__"
//    Output: "This is <strong>bold</strong>, this is <em>italic</em>, and this is <u>underlined</u>"
//
// 3. Lists and formatting:
//    Input:  "### Key Points\n- First point\n- Second point\n1. Step one\n2. Step two"
//    Output: "<h4>Key Points</h4><ul><li>First point</li><li>Second point</li></ul><ol><li>Step one</li><li>Step two</li></ol>"
//
// 4. Quotes and links:
//    Input:  "> This is an important quote\nVisit [our website](https://example.com) for more info\n---\nEnd of section"
//    Output: "<blockquote>This is an important quote</blockquote>Visit <a href='https://example.com'>our website</a> for more info<hr>End of section"
//
// 5. In React components:
//    <div dangerouslySetInnerHTML={renderMarkdownToHTML(course.description)} />
//
// ============================================================================
// WHERE TO USE THIS SYSTEM
// ============================================================================
//
// ✅ Course descriptions (CourseOverview, CourseEdit)
// ✅ Lesson content descriptions
// ✅ Prompt descriptions
// ✅ Organization descriptions
// ✅ Any user-generated content that needs formatting
//
// ❌ Don't use for:
//    - User input validation (security risk)
//    - System-generated content (unnecessary)
//    - URLs or links (not supported yet)
//
// ============================================================================
// SECURITY CONSIDERATIONS
// ============================================================================
//
// This renderer only converts specific markdown patterns to safe HTML tags.
// It does NOT support:
// - Script tags or JavaScript execution
// - HTML attributes or complex HTML
// - External links or iframes
// - Any potentially dangerous HTML
//
// Always use with React's dangerouslySetInnerHTML in controlled contexts.
//
// ============================================================================
// EXTENDING THE SYSTEM
// ============================================================================
//
// To add new formatting options:
// 1. Add the pattern to the renderMarkdown function
// 2. Update this documentation
// 3. Test with various input scenarios
// 4. Consider security implications
//
// Example extension for lists:
// - Convert "- item" to "<li>item</li>"
// - Wrap in <ul> tags
//
// ============================================================================
// PERFORMANCE NOTES
// ============================================================================
//
// - Uses simple regex replacement (fast for small to medium text)
// - Processes text once per render
// - Consider memoization for large, static content
// - Avoid calling in render loops without useMemo/useCallback
//
// ============================================================================
// TESTING
// ============================================================================
//
// Test cases to verify:
// - Empty/null input handling
// - Mixed formatting combinations
// - Special characters in formatted text
// - Very long text performance
// - Edge cases with malformed markdown

/**
 * Converts markdown-style formatting to HTML
 * Supports: **bold**, *italic*, __underline__, ### headlines, lists, links, horizontal lines, line breaks
 * 
 * @param {string} text - The markdown text to convert
 * @returns {string} - HTML string with proper formatting
 * 
 * @example
 * renderMarkdown("Learn **essential skills** and *techniques*")
 * // Returns: "Learn <strong>essential skills</strong> and <em>techniques</em>"
 */
export const renderMarkdown = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  let html = text;

  // Convert ### headlines to <h4> tags
  // Matches: ### Headline → <h4 class="text-lg font-semibold mt-2 mb-1">Headline</h4>
  // Note: Color is applied via parent component's theme colors
  html = html.replace(/^###\s*(.*$)/gm, '<h4 class="text-lg font-semibold mt-2 mb-1">$1</h4>');

  // Convert > quotes to <blockquote> tags
  // Matches: > quote text → <blockquote class="border-l-4 border-gray-300 pl-4 italic text-gray-700 my-2">quote text</blockquote>
  html = html.replace(/^> (.*$)/gm, '<blockquote class="border-l-4 border-gray-300 pl-4 italic text-gray-700 my-2">$1</blockquote>');

  // Convert **bold** to <strong> tags
  // Matches: **text** → <strong>text</strong>
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Convert *italic* to <em> tags
  // Matches: *text* → <em>text</em>
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Convert __underline__ to <u> tags
  // Matches: __text__ → <u>text</u>
  html = html.replace(/__(.*?)__/g, '<u>$1</u>');

  // Convert horizontal lines: ---
  // Matches: --- → <hr class="my-2 border-gray-300">
  html = html.replace(/^---$/gm, '<hr class="my-2 border-gray-300">');

  // Convert hyperlinks: [text](url) — only allow http/https schemes
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
    const safeUrl = sanitizeUrl(url);
    if (!safeUrl) return text;
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${text}</a>`;
  });

  // Convert bullet points: - item or • item
  // First convert individual list items
  html = html.replace(/^[-•]\s+(.*)$/gm, '<li class="bullet-item" style="padding-left: 1.5rem; position: relative; text-indent: 0;">$1</li>');
  // Then wrap consecutive bullet list items in <ul> tags
  html = html.replace(/(<li class="bullet-item" style="padding-left: 1\.5rem; position: relative; text-indent: 0;">.*?<\/li>)(\s*<li class="bullet-item" style="padding-left: 1\.5rem; position: relative; text-indent: 0;">.*?<\/li>)*/gs, (match) => {
    // Add bullet marker before each list item
    match = match.replace(/<li class="bullet-item" style="padding-left: 1\.5rem; position: relative; text-indent: 0;">/g, '<li class="bullet-item" style="padding-left: 1.5rem; position: relative; text-indent: 0;"><span style="position: absolute; left: 0;">•</span>');
    return `<ul class="mb-2" style="list-style: none; padding-left: 0;">${match}</ul>`;
  });

  // Convert numbered lists: 1. item
  // First convert individual list items
  html = html.replace(/^(\d+)\.\s+(.*)$/gm, '<li class="numbered-item" style="padding-left: 1.5rem; position: relative; text-indent: 0;">$2</li>');
  // Then wrap consecutive numbered list items in <ol> tags
  html = html.replace(/(<li class="numbered-item" style="padding-left: 1\.5rem; position: relative; text-indent: 0;">.*?<\/li>)(\s*<li class="numbered-item" style="padding-left: 1\.5rem; position: relative; text-indent: 0;">.*?<\/li>)*/gs, (match) => {
    // Add numbers before each list item
    let counter = 1;
    match = match.replace(/<li class="numbered-item" style="padding-left: 1\.5rem; position: relative; text-indent: 0;">/g, () => {
      return `<li class="numbered-item" style="padding-left: 1.5rem; position: relative; text-indent: 0;"><span style="position: absolute; left: 0;">${counter++}.</span>`;
    });
    return `<ol class="mb-2" style="list-style: none; padding-left: 0;">${match}</ol>`;
  });

  // Convert line breaks to <br> tags, but avoid adding them inside list elements
  // Process lists first to avoid adding <br> tags inside them
  const listPattern = /<(ul|ol)[^>]*>.*?<\/\1>/gs;
  const lists = [];
  let listIndex = 0;
  
  // Extract all lists and replace with placeholders
  html = html.replace(listPattern, (match) => {
    const placeholder = `__LIST_PLACEHOLDER_${listIndex}__`;
    lists[listIndex] = match;
    listIndex++;
    return placeholder;
  });
  
  // Convert line breaks to <br> tags in the remaining content
  html = html.replace(/\n/g, '<br>');
  
  // Restore the lists (which don't have <br> tags)
  lists.forEach((list, index) => {
    html = html.replace(`__LIST_PLACEHOLDER_${index}__`, list);
  });

  return html;
};

/**
 * Safely renders markdown text as HTML for React
 * This function returns an object with __html property for dangerouslySetInnerHTML
 * 
 * @param {string} text - The markdown text to convert
 * @returns {object} - Object with __html property for React
 * 
 * @example
 * <div dangerouslySetInnerHTML={renderMarkdownToHTML(course.description)} />
 */
export const renderMarkdownToHTML = (text) => {
  return {
    __html: renderMarkdown(text)
  };
};

/**
 * Strips markdown formatting from text, returning plain text
 * Useful for generating summaries or search indexes
 * 
 * @param {string} text - The markdown text to strip
 * @returns {string} - Plain text without formatting
 * 
 * @example
 * stripMarkdown("Learn **essential skills** and *techniques*")
 * // Returns: "Learn essential skills and techniques"
 */
export const stripMarkdown = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  let plainText = text;

  // Remove all markdown formatting
  plainText = plainText.replace(/^###\s*(.*$)/gm, '$1');     // Remove ### headlines
  plainText = plainText.replace(/^> (.*$)/gm, '$1');       // Remove > quotes
  plainText = plainText.replace(/\*\*(.*?)\*\*/g, '$1');  // Remove **bold**
  plainText = plainText.replace(/\*(.*?)\*/g, '$1');       // Remove *italic*
  plainText = plainText.replace(/__(.*?)__/g, '$1');       // Remove __underline__
  plainText = plainText.replace(/^---$/gm, '');            // Remove horizontal lines
  plainText = plainText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // Remove links, keep text
  plainText = plainText.replace(/^[-•]\s+(.*)$/gm, '$1');  // Remove bullet points
  plainText = plainText.replace(/^\d+\.\s+(.*)$/gm, '$1'); // Remove numbered lists

  return plainText;
};

/**
 * Checks if text contains any markdown formatting
 * Useful for conditional rendering or validation
 * 
 * @param {string} text - The text to check
 * @returns {boolean} - True if markdown formatting is found
 * 
 * @example
 * hasMarkdown("Plain text") // Returns: false
 * hasMarkdown("**Bold text**") // Returns: true
 */
export const hasMarkdown = (text) => {
  if (!text || typeof text !== 'string') {
    return false;
  }

  // Check for any markdown patterns
  return /^###\s*.*|^>.*|\*\*.*?\*\*|\*.*?\*|__.*?__|^---$|\[.*?\]\(.*?\)|^[-•]\s+.*|^\d+\.\s+.*/.test(text);
};
